import React, { useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  MessageSquare, Users, Flame, Sparkles, Compass, Camera, Crown, Rocket,
} from "lucide-react";
import { CaptionPack } from "./FreeVsProReel";

/**
 * /share/join-the-chat — Community engagement reel.
 *
 * Pitch: "browse the app, leave comments on the threads, meet other UK
 * campers". Three 9:16 scenes designed to convert lurkers into commenters
 * on the Campfire forum.
 *
 *   Scene 1 — "The Campfire is open" community hero
 *   Scene 2 — Three thread snippets ("real questions, real campers")
 *   Scene 3 — QR + "Leave a comment" CTA
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const COMMUNITY_URL = `${PUBLIC_URL}/community?ref=join-the-chat-reel`;

function Sticker({ children, color = "#ff4500", textColor = "#ffffff" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em]"
      style={{ background: color, color: textColor }}
    >
      {children}
    </span>
  );
}

function Scene({ children, bg, testid, label }) {
  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500]">{label}</div>
      <section
        className="relative w-full mx-auto flex flex-col justify-between text-white overflow-hidden"
        style={{
          aspectRatio: "9 / 16",
          maxWidth: 480,
          backgroundImage: bg,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid={testid}
      >
        {children}
        <div
          className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/65 backdrop-blur-sm text-white text-[10px] sm:text-[11px] font-bold mono uppercase tracking-[0.22em] text-center pointer-events-none z-10"
          aria-hidden="true"
        >
          🌐 camp-compass.uk
        </div>
      </section>
    </div>
  );
}

const THREADS = [
  { title: "What's the one bit of kit you'd never camp without?", replies: 47, tag: "Gear & Kit" },
  { title: "Best UK campsite for a first-time family trip?", replies: 32, tag: "Family" },
  { title: "Off-grid wild camping — where do you go?", replies: 28, tag: "Wild" },
  { title: "Where's your next UK trip taking you?", replies: 51, tag: "Planning" },
  { title: "Introduce yourself — say hi 👋", replies: 89, tag: "General" },
];

export default function JoinTheChatReel() {
  const captions = useMemo(() => [
    {
      title: "Caption · 'Come find your camping people'",
      body: `🔥 The Campfire is OPEN.

CampCompass UK isn't just a campsite finder — it's a community of UK campers swapping tips, trip reports and gear hacks every day.

47 replies on "what's the one bit of kit you'd never camp without"
51 replies on "where's your next UK trip taking you"
89 hellos on the welcome thread

Bring your daftest camping story. We'll bring the bench. ⛺🔥

👉 camp-compass.uk/community

Free to read, free to post. No algorithm, no ads.

#ukcamping #campervan #ukcamping2026 #campingcommunity`,
    },
    {
      title: "Caption · TikTok angle (lurkers convert)",
      body: `New UK camping forum dropped 👇

No algorithm. No ads. No "buy now" buttons. Just UK campers swapping notes — gear hacks, hidden sites, trip reports.

Browse free. Leave a comment if it sparks something. Make a friend.

👉 camp-compass.uk/community

#ukcamping #campingweekend #ukcampsites`,
    },
    {
      title: "Caption · Personal invite for stories",
      body: `Come say hi at the Campfire 🔥

Took us months to build this. Real questions, real campers, real answers.

👉 camp-compass.uk/community

Drop your "introduce yourself" message — we'll all wave back.

#ukcamping`,
    },
  ], []);

  return (
    <div className="bg-[#0e0e0e] min-h-screen text-white" data-testid="join-the-chat-page">
      <header className="max-w-3xl mx-auto px-5 py-8">
        <div className="eyebrow text-[#ff4500] mb-2 flex items-center gap-2">
          <Flame className="w-3.5 h-3.5" /> Campfire community reel
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mb-3">The Campfire<br />is open 🔥</h1>
        <p className="text-sm text-[#bbb] leading-relaxed max-w-xl">
          Three 9:16 scenes pointed at the Campfire community. Convert browsers into commenters.
          Use this reel when you want signups + early UGC, not just page views.
        </p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 px-3 pb-10 max-w-7xl mx-auto justify-items-center">

        {/* SCENE 1 — Campfire hero */}
        <Scene
          label="Scene 1 · the campfire"
          testid="jc-scene-1"
          bg='linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(255,69,0,0.45) 60%, rgba(0,0,0,0.85) 100%), url("https://images.unsplash.com/photo-1455587734955-081b22074882?w=1080&h=1920&fit=crop&auto=format&q=80")'
        >
          <div className="p-6 pt-10 flex justify-between gap-2">
            <Sticker color="#ffffff" textColor="#0e0e0e">
              <Flame className="w-3.5 h-3.5" /> The Campfire
            </Sticker>
            <Sticker color="#ff4500">
              <Users className="w-3.5 h-3.5" /> Free
            </Sticker>
          </div>
          <div className="px-6 text-center">
            <div className="font-display text-[60px] sm:text-[78px] leading-[0.88] drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]">
              YOUR<br />CAMPING<br />PEOPLE.
            </div>
            <div className="inline-block font-display text-2xl sm:text-3xl px-3 py-1 mt-3" style={{ background: "#ff4500", color: "#fff" }}>
              Now open
            </div>
          </div>
          <div className="p-6 pb-8 text-center">
            <div className="text-xs sm:text-sm mono uppercase tracking-[0.22em] text-white/95">
              247 hellos · 89 replies · 5 live threads
            </div>
            <div className="text-[11px] mono uppercase tracking-[0.22em] text-[#ffe7d6] mt-1">
              Come share your daftest camping story 👇
            </div>
          </div>
        </Scene>

        {/* SCENE 2 — Thread preview */}
        <Scene
          label="Scene 2 · real threads"
          testid="jc-scene-2"
          bg="linear-gradient(180deg, #0e0e0e 0%, #1a1a1a 100%)"
        >
          <div className="p-5 pt-8">
            <Sticker color="#ff4500">
              <MessageSquare className="w-3.5 h-3.5" /> Real campers
            </Sticker>
            <div className="font-display text-[28px] sm:text-[34px] leading-[0.95] mt-3">
              Real questions.<br />Real answers.
            </div>
          </div>
          <div className="px-4 space-y-2">
            {THREADS.map((t, i) => (
              <div key={i} className="border border-white/20 bg-white/8 p-2.5">
                <div className="text-[10px] mono uppercase tracking-[0.18em] text-[#ffe7d6] mb-1">{t.tag}</div>
                <div className="text-[12px] sm:text-[13px] font-display leading-snug">{t.title}</div>
                <div className="text-[10px] mono uppercase tracking-[0.18em] text-white/75 mt-1">
                  {t.replies} replies
                </div>
              </div>
            ))}
          </div>
          <div className="p-5 text-center">
            <div className="text-[11px] mono uppercase tracking-[0.22em] text-[#ffe7d6]">
              Drop your reply 👇
            </div>
          </div>
        </Scene>

        {/* SCENE 3 — Comment CTA QR */}
        <Scene
          label="Scene 3 · leave a comment"
          testid="jc-scene-3"
          bg="linear-gradient(180deg, #ff4500 0%, #b73200 70%, #0e0e0e 100%)"
        >
          <div className="p-6 pt-10 flex justify-between gap-2">
            <Sticker color="#ffffff" textColor="#ff4500">
              <Compass className="w-3.5 h-3.5" /> Try it
            </Sticker>
            <Sticker color="#0e0e0e">
              <MessageSquare className="w-3.5 h-3.5" /> Comment
            </Sticker>
          </div>
          <div className="px-6 text-center">
            <div className="font-display text-[48px] sm:text-[60px] leading-[0.92] drop-shadow-[0_4px_24px_rgba(0,0,0,0.5)]">
              BROWSE.<br />COMMENT.<br />CONNECT.
            </div>
            <div className="text-sm sm:text-base text-white/95 mt-3 max-w-[280px] mx-auto leading-snug">
              No algorithm. No ads. Just UK campers swapping notes.
            </div>
          </div>
          <div className="p-4 space-y-2" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.9) 100%)" }}>
            <div className="flex items-center justify-between gap-3">
              <div className="text-left min-w-0">
                <div className="text-[10px] mono uppercase tracking-[0.22em] text-white/90 mb-1">Open the forum</div>
                <div className="font-display text-sm sm:text-base leading-tight break-all">camp-compass.uk/community</div>
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-[#ffe7d6] mt-1">Free · no signup to read</div>
              </div>
              <div className="bg-white p-1.5 shrink-0" data-testid="jc-qr">
                <QRCodeSVG value={COMMUNITY_URL} size={100} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
              </div>
            </div>
          </div>
        </Scene>
      </div>

      <CaptionPack captions={captions} testid="jc-captions" />
    </div>
  );
}
